<template>
  <Loading v-if="componentLoading" :style="Object.assign({ height: '24px' }, style)" />
  <template v-else>
    <template v-if="theme === 'normal'">
      <view class="pft-weather-component" :flex="flex" :style="style">
        <img v-if="icon" :src="icon" />
        <view class="weather-string">
          {{ nowData.text }}
          <template v-if="showTemp">{{ nowData.temp }}℃</template>
        </view>
        <view v-if="showHumidity" class="weather-string humidity">
          <img v-if="showHumidityIcon" :src="WetIcon" class="humidity-icon" />
          <span>{{ nowData.humidity }}%</span>
        </view>
        <view v-if="aqiData && parseInt(aqiData.aqi as string) < aqiLimited" class="weather-string aqi"> - {{ aqiData.category }} </view>
        <view v-if="warningList.length > 0" class="warning-container">
          <TextFliper :front-text="frontText" :back-text="backText" />
        </view>
      </view>
    </template>
    <template v-else-if="theme === 'column'">
      <view class="pft-weather-component column" flex="dir:top main:center cross:center" :style="style">
        <view class="weather-string" flex="dir:left main:center cross:bottom">
          <view v-if="showTemp" class="temperature" flex="dir:left main:center cross:top">
            <view>{{ nowData.temp }}</view>
            <view class="suffix">℃</view>
          </view>
          <view v-if="showHumidity" flex="dir:left main:center cross:center" class="info-line ml8">
            <img v-if="showHumidityIcon" :src="WetIcon" />
            <view>{{ nowData.humidity }}%</view>
          </view>
        </view>
        <view flex="dir:left main:center cross:center" class="info-line mb8">
          <img v-if="icon" :src="icon" />
          <view>{{ nowData.text }}</view>
          <view v-if="aqiData && parseInt(aqiData.aqi as string) < aqiLimited" class="aqi"> - {{ aqiData.category }} </view>
        </view>

        <view v-if="warningList.length > 0" class="warning-container">
          <TextFliper :front-text="frontText" :back-text="backText" />
        </view>
      </view>
    </template>
    <template v-else-if="theme === 'customer'">
      <view class="pft-weather-component" :style="style">
        <Teleport v-if="tempRef && showTemp" :to="tempRef">
          <view class="temperature" flex="dir:left main:center cross:top">
            <view>{{ nowData.temp }}</view>
            <view class="suffix">℃</view>
          </view>
        </Teleport>
        <Teleport v-if="iconRef" :to="iconRef">
          <view class="weather" :flex="weatherFlex">
            <img v-if="icon" :src="icon" :style="weatcherIcon" />
            <view>{{ nowData.text }}</view>
          </view>
        </Teleport>
        <Teleport v-if="aqiRef" :to="aqiRef">
          <view v-if="aqiData && parseInt(aqiData.aqi as string) < aqiLimited" class="aqi"> {{ aqiData.category }} </view>
        </Teleport>
        <Teleport v-if="wetRef && showHumidity" :to="wetRef">
          <view :flex="wetFlex" class="info-line ml8">
            <img :src="WetIcon" :style="wetIcon" />
            <view>{{ nowData.humidity }}%</view>
          </view>
        </Teleport>
        <Teleport v-if="warningRef" :to="warningRef">
          <view v-if="warningList.length > 0" class="warning-container">
            <TextFliper :front-text="frontText" :back-text="backText" />
          </view>
        </Teleport>
        <view ref="layoutRef" :flex="flex">
          <slot />
        </view>
      </view>
    </template>
  </template>
</template>

<script lang="ts" setup>
import { getWwatherReq } from "@p-v/api"
import { computed, nextTick, onMounted, ref } from "vue"
import Loading from "@p-v/packages/Loading.vue"
import TextFliper from "@p-v/packages/TextFliper/index.vue"
import WetIcon from "@p-v/asset/image/weather/wet.png"

const layoutRef = ref<HTMLElement | null>(null)

const tempRef = ref<HTMLCollection | undefined>(undefined)
const iconRef = ref<HTMLCollection | null>(null)
const aqiRef = ref<HTMLCollection | null>(null)
const wetRef = ref<HTMLCollection | null>(null)
const warningRef = ref<HTMLCollection | null>(null)

// https://12301-cc.feishu.cn/wiki/AFaiwCFUSiNxDikBHcKcYCZPnce

//异步 import src/asset/image/weather离得所有图片
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
const weatherIcon = import.meta.glob("@p-v/asset/image/weather/*.png")
console.log("weatherIcon", weatherIcon)
const icons = ref<{ [key: string]: () => Promise<{ default: string }> }>({})
let icon = ref<string | undefined>(undefined)
let iconPromise: Promise<void>[] = []
Object.keys(weatherIcon).forEach((name: string) => {
  const matchRes = name.match(/weather\/(.*)\.png/)
  if (matchRes && matchRes.length > 1) {
    const key = matchRes[1]
    icons.value[key] = weatherIcon[name]
  }
})

defineOptions({
  name: "PftWeather",
})

interface ComponentProps {
  cityCode: number | string
  style?: { [key: string]: string }
  flex?: string
  aqi?: boolean
  aqiLimited?: number
  warning?: boolean
  theme?: string
  time: number
  weatherFlex?: string
  wetFlex?: string
  weatcherIconSize?: string
  wetIconSize?: string
  api?: (...data: any[]) => Promise<any>
  resultKey?: string
  showTemp?: boolean
  showHumidity?: boolean
  showHumidityIcon?: boolean
}

const props = withDefaults(defineProps<ComponentProps>(), {
  cityCode: () => 101280601,
  style: () => ({}),
  flex: () => "dir:left cross:center",
  aqi: () => true,
  aqiLimited: () => Number.MAX_SAFE_INTEGER,
  warning: () => false,
  time: () => 5000,
  theme: () => "normal",
  weatherFlex: () => "dir:left main:center cross:center",
  wetFlex: () => "dir:left main:center cross:center",
  weatcherIconSize: () => "25px",
  wetIconSize: () => "14px",
  api: getWwatherReq,
  resultKey: () => "result",
  showTemp: () => true,
  showHumidity: () => false,
  showHumidityIcon: () => true,
  // tempRef: undefined,
  // iconRef: undefined,
  // aqiRef: undefined,
  // wetRef: undefined,
  // warningRef: undefined,
})

const weatcherIcon = computed(() => {
  return {
    width: props.weatcherIconSize,
    height: props.weatcherIconSize,
  }
})

const wetIcon = computed(() => {
  return {
    width: props.wetIconSize,
    height: props.wetIconSize,
  }
})

const nowData = ref<{ [key: string]: string | number }>({})
const aqiData = ref<{ [key: string]: string | number }>({})
const componentLoading = ref<boolean>(true)

interface WarningMsg {
  id?: string
  sender?: string
  pubTime?: string
  title?: string
  startTime?: string
  endTime?: string
  status?: string
  level?: string
  severity?: string
  severityColor: "White" | "Blue" | "Green" | "Yellow" | "Orange" | "Red" | "Black"
  type?: string
  typeName: string
  urgency?: string
  certainty?: string
  text?: string
  related?: string
}
const warningList = ref<WarningMsg[]>([])
const warningIndex = ref(0)

const colorList = {
  Red: {
    background: "#f9614a",
    color: "#ffffff",
  },
  Yellow: {
    background: "#eaca41",
    color: "#000000",
  },
  White: {
    background: "#ffffff",
    color: "#000000",
  },
  Blue: {
    background: "#2bb7f7",
    color: "#ffffff",
  },
  Green: {
    background: "#63e7b7",
    color: "#ffffff",
  },
  Orange: {
    background: "#ffa200",
    color: "#ffffff",
  },
  Black: {
    background: "#000000",
    color: "#ffffff",
  },
}

const frontText = ref<any>(undefined)

const backText = ref<any>(undefined)

const styleElement = ref<HTMLStyleElement | null>(null)

let flipTimer: any = null

const getReslut = (body: any) => {
  let result = body
  if (props.resultKey) {
    const keys = props.resultKey.split(".")
    keys.forEach(key => {
      result = result[key]
    })
  }
  return result
}

const updateWeather = async () => {
  try {
    const data = await props
      .api(props.cityCode, {
        aqi: props.aqi,
        warning: props.warning,
      })
      .catch(e => {
        console.error(e)
        return {
          result: {
            now: {
              obsTime: "2024-11-21T10:40+08:00",
              temp: "15",
              feelsLike: "12",
              icon: "100",
              text: "晴",
              wind360: "225",
              windDir: "西南风",
              windScale: "2",
              windSpeed: "8",
              humidity: "27",
              precip: "0.0",
              pressure: "992",
              vis: "16",
              cloud: "0",
              dew: "-1",
            },
            aqi: {
              aqi: 20,
              category: "优",
            },
            warning: [
              {
                id: "1",
                sender: "中央气象台",
                pubTime: "2021-09-01 12:00",
                title: "台风预警",
                startTime: "2021-09-01 12:00",
                endTime: "2021-09-02 12:00",
                status: "预警中",
                level: "红色",
                severity: "红色",
                severityColor: "Red",
                type: "台风",
                typeName: "台风",
                urgency: "紧急",
                certainty: "确定",
                text: "台风预警",
                related: "无",
              },
              {
                id: "2",
                sender: "中央气象台",
                pubTime: "2021-09-01 12:00",
                title: "台风预警",
                startTime: "2021-09-01 12:00",
                endTime: "2021-09-02 12:00",
                status: "预警中",
                level: "红色",
                severity: "红色",
                severityColor: "Red",
                type: "暴雨",
                typeName: "暴雨",
                urgency: "紧急",
                certainty: "确定",
                text: "暴雨预警",
                related: "无",
              },
            ],
          },
        }
      })

    console.log(data, componentLoading)

    if (data) {
      try {
        const result = getReslut(data)
        nowData.value = result.now
        if (icons.value[nowData.value.icon]) {
          icons.value[nowData.value.icon]().then(res => {
            icon.value = res.default
          })
        }
        aqiData.value = result.aqi
        warningList.value = result.warning || []
        if (flipTimer) clearInterval(flipTimer)

        componentLoading.value = false
        let curIndex = warningIndex.value
        let nextIndex = curIndex
        if (warningList.value.length > 0) {
          curIndex = warningIndex.value
          nextIndex = curIndex === warningList.value.length - 1 ? 0 : curIndex + 1
          frontText.value = {
            text: warningList.value[curIndex].typeName,
            color: colorList[warningList.value[curIndex].severityColor].color,
            background: colorList[warningList.value[curIndex].severityColor].background,
          }
          backText.value = {
            text: warningList.value[nextIndex].typeName,
            color: colorList[warningList.value[nextIndex].severityColor].color,
            background: colorList[warningList.value[nextIndex].severityColor].background,
          }
        }
        if (warningList.value.length > 1) {
          flipTimer = setInterval(() => {
            curIndex = curIndex === warningList.value.length - 1 ? 0 : curIndex + 1
            nextIndex = curIndex === warningList.value.length - 1 ? 0 : curIndex + 1
            frontText.value = {
              text: warningList.value[curIndex].typeName,
              color: colorList[warningList.value[curIndex].severityColor].color,
              background: colorList[warningList.value[curIndex].severityColor].background,
            }
            backText.value = {
              text: warningList.value[nextIndex].typeName,
              color: colorList[warningList.value[nextIndex].severityColor].color,
              background: colorList[warningList.value[nextIndex].severityColor].background,
            }
          }, props.time)
        }
      } catch (e) {
        console.error(e)
      }
    }

    //每小时更新一次
    setTimeout(
      () => {
        updateWeather()
      },
      60 * 60 * 1000,
    )
  } catch (e) {
    console.error(e)
  }
}

const findComponent = (el: HTMLElement | Element, type: string) => {
  console.log(el.attributes["type"]?.nodeValue, type)
  if (el.attributes["type"]?.nodeValue === type) {
    return el
  }
  if (el.children.length > 0) {
    for (let i = 0; i < el.children.length; i++) {
      const res = findComponent(el.children[i], type)
      if (res) {
        return res
      }
    }
  }
  return undefined
}

onMounted(async () => {
  styleElement.value = document.createElement("style")
  document.head.appendChild(styleElement.value)
  await Promise.all(iconPromise)
  // TODO: 修改为配置
  await updateWeather()

  nextTick(() => {
    if (layoutRef.value) {
      tempRef.value = findComponent(layoutRef.value, "temperature")
      iconRef.value = findComponent(layoutRef.value, "weather")
      aqiRef.value = findComponent(layoutRef.value, "aqi")
      wetRef.value = findComponent(layoutRef.value, "wet")
      warningRef.value = findComponent(layoutRef.value, "warning")
    }
  })
})
</script>

<style lang="scss">
.pft-weather-component {
  img {
    width: 25px;
    height: 25px;
    margin-right: 8px;
  }
  .weather-string {
    font-family:
      Segoe UI,
      Segoe UI;
    font-weight: bold;
    font-size: 16px;
    color: #a6f1fe;
    line-height: 24px;
    text-shadow: 0px 0px 8px #2b97f7;
    &.aqi {
      margin-left: 5px;
    }
    &.humidity {
      margin-left: 10px;
      display: flex;
      align-items: center;

      .humidity-icon {
        width: 16px;
        height: 16px;
        margin-right: 4px;
      }
    }
  }
  &.column {
    .weather-string {
      font-size: 24px;
      color: #fff;
      text-shadow: 0px 0px 8px #fff;
      margin-bottom: 8px;
      font-weight: 700;
      .suffix {
        font-size: 12px;
        line-height: 12px;
        margin-left: 2px;
      }
    }
    .info-line {
      line-height: 14px;
      img {
        width: 14px;
        height: 14px;
        margin-right: 2px;
      }
      view {
        font-size: 12px;
        color: #fff;
      }
      .aqi {
        margin-left: 5px;
      }
      &.mb8 {
        margin-bottom: 8px;
      }
      &.ml8 {
        margin-left: 8px;
      }
    }
  }
}

.warning-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 5px;
}
</style>
